/**
 * Track Plugin Presets
 * Factory presets and user preset management for individual track plugin chains
 * 
 * Unlike master bus presets which are global, track presets are categorized by:
 * - Track Type (Vocals, Drums, Bass, Guitar, Keys, etc.)
 * - Processing Goal (Clean, Aggressive, Warm, etc.)
 */

// Factory Track Presets - Pre-configured plugin chains by instrument type
export const FACTORY_TRACK_PRESETS = {
  // === VOCALS ===
  'vocal-clean': {
    id: 'vocal-clean',
    name: 'Clean Vocals',
    description: 'Natural, clear vocal processing',
    category: 'vocals',
    icon: 'mic',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 80, lowGain: -6,
          mid1Freq: 300, mid1Gain: -2, mid1Q: 1.5,
          mid2Freq: 3000, mid2Gain: 2, mid2Q: 1,
          highFreq: 10000, highGain: 1.5
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -18, ratio: 3, attack: 0.01, release: 0.15,
          knee: 6, makeup: 3, mix: 1
        }
      }
    ]
  },
  
  'vocal-warm': {
    id: 'vocal-warm',
    name: 'Warm Vocals',
    description: 'Rich, full-bodied vocal tone',
    category: 'vocals',
    icon: 'mic',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 100, lowGain: 1,
          mid1Freq: 200, mid1Gain: 1.5, mid1Q: 1,
          mid2Freq: 2500, mid2Gain: 1, mid2Q: 0.8,
          highFreq: 8000, highGain: -1
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -20, ratio: 2.5, attack: 0.015, release: 0.2,
          knee: 8, makeup: 2, mix: 0.8
        }
      },
      {
        type: 'reverb',
        params: { decay: 1.2, preDelay: 0.02, damping: 6000, size: 0.4, mix: 0.15 }
      }
    ]
  },
  
  'vocal-presence': {
    id: 'vocal-presence',
    name: 'Vocal Presence',
    description: 'Cut through the mix with clarity',
    category: 'vocals',
    icon: 'mic',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 120, lowGain: -4,
          mid1Freq: 400, mid1Gain: -1.5, mid1Q: 1.5,
          mid2Freq: 3500, mid2Gain: 3, mid2Q: 1,
          highFreq: 12000, highGain: 2
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -15, ratio: 4, attack: 0.005, release: 0.1,
          knee: 4, makeup: 4, mix: 1
        }
      }
    ]
  },
  
  'vocal-radio': {
    id: 'vocal-radio',
    name: 'Radio Ready',
    description: 'Broadcast-style vocal processing',
    category: 'vocals',
    icon: 'radio',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 100, lowGain: -8,
          mid1Freq: 300, mid1Gain: -2, mid1Q: 2,
          mid2Freq: 4000, mid2Gain: 2.5, mid2Q: 1,
          highFreq: 10000, highGain: 2
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -12, ratio: 6, attack: 0.003, release: 0.08,
          knee: 3, makeup: 5, mix: 1
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -6, ratio: 10, attack: 0.001, release: 0.05,
          knee: 0, makeup: 2, mix: 1
        }
      }
    ]
  },

  // === DRUMS ===
  'drums-punchy': {
    id: 'drums-punchy',
    name: 'Punchy Drums',
    description: 'Tight, impactful drum sound',
    category: 'drums',
    icon: 'drum',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 60, lowGain: 2,
          mid1Freq: 400, mid1Gain: -2, mid1Q: 2,
          mid2Freq: 3000, mid2Gain: 2, mid2Q: 1,
          highFreq: 8000, highGain: 1.5
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -12, ratio: 4, attack: 0.001, release: 0.08,
          knee: 3, makeup: 4, mix: 1
        }
      }
    ]
  },
  
  'drums-room': {
    id: 'drums-room',
    name: 'Room Sound',
    description: 'Natural room ambience for drums',
    category: 'drums',
    icon: 'drum',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 80, lowGain: 1,
          mid1Freq: 500, mid1Gain: -1, mid1Q: 1,
          mid2Freq: 2500, mid2Gain: 1.5, mid2Q: 0.8,
          highFreq: 10000, highGain: 1
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -18, ratio: 3, attack: 0.02, release: 0.2,
          knee: 6, makeup: 2, mix: 0.7
        }
      },
      {
        type: 'reverb',
        params: { decay: 0.6, preDelay: 0.01, damping: 6000, size: 0.3, mix: 0.25 }
      }
    ]
  },
  
  'drums-808': {
    id: 'drums-808',
    name: '808 Enhance',
    description: 'Heavy sub-bass for hip-hop drums',
    category: 'drums',
    icon: 'drum',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 50, lowGain: 4,
          mid1Freq: 200, mid1Gain: -2, mid1Q: 1.5,
          mid2Freq: 4000, mid2Gain: 1, mid2Q: 1,
          highFreq: 8000, highGain: 2
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -15, ratio: 5, attack: 0.01, release: 0.15,
          knee: 4, makeup: 3, mix: 1
        }
      }
    ]
  },

  // === BASS ===
  'bass-clean': {
    id: 'bass-clean',
    name: 'Clean Bass',
    description: 'Clear, defined low-end',
    category: 'bass',
    icon: 'music',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 60, lowGain: 2,
          mid1Freq: 300, mid1Gain: -1, mid1Q: 1,
          mid2Freq: 800, mid2Gain: 1, mid2Q: 1.5,
          highFreq: 4000, highGain: -2
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -15, ratio: 4, attack: 0.02, release: 0.15,
          knee: 6, makeup: 2, mix: 1
        }
      }
    ]
  },
  
  'bass-growl': {
    id: 'bass-growl',
    name: 'Growling Bass',
    description: 'Aggressive, midrange-focused bass',
    category: 'bass',
    icon: 'music',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 80, lowGain: 1,
          mid1Freq: 500, mid1Gain: 2, mid1Q: 1.2,
          mid2Freq: 1500, mid2Gain: 3, mid2Q: 1,
          highFreq: 6000, highGain: 1
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -12, ratio: 5, attack: 0.005, release: 0.1,
          knee: 4, makeup: 4, mix: 1
        }
      }
    ]
  },
  
  'bass-sub': {
    id: 'bass-sub',
    name: 'Sub Bass',
    description: 'Deep, thundering low frequencies',
    category: 'bass',
    icon: 'music',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 40, lowGain: 4,
          mid1Freq: 150, mid1Gain: 1, mid1Q: 1.5,
          mid2Freq: 500, mid2Gain: -3, mid2Q: 1,
          highFreq: 2000, highGain: -6
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -18, ratio: 3, attack: 0.03, release: 0.2,
          knee: 8, makeup: 2, mix: 0.8
        }
      }
    ]
  },

  // === GUITAR ===
  'guitar-clean': {
    id: 'guitar-clean',
    name: 'Clean Guitar',
    description: 'Sparkling clean guitar tone',
    category: 'guitar',
    icon: 'guitar',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 100, lowGain: -2,
          mid1Freq: 400, mid1Gain: -1, mid1Q: 1,
          mid2Freq: 2500, mid2Gain: 2, mid2Q: 0.8,
          highFreq: 8000, highGain: 1.5
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -20, ratio: 2, attack: 0.02, release: 0.2,
          knee: 10, makeup: 1.5, mix: 0.6
        }
      },
      {
        type: 'chorus',
        params: { rate: 0.5, depth: 0.002, mix: 0.2 }
      }
    ]
  },
  
  'guitar-crunch': {
    id: 'guitar-crunch',
    name: 'Crunchy Guitar',
    description: 'Slightly overdriven rock tone',
    category: 'guitar',
    icon: 'guitar',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 120, lowGain: 0,
          mid1Freq: 500, mid1Gain: 1, mid1Q: 1,
          mid2Freq: 3000, mid2Gain: 2, mid2Q: 1,
          highFreq: 6000, highGain: 1
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -15, ratio: 3, attack: 0.01, release: 0.15,
          knee: 6, makeup: 3, mix: 0.8
        }
      },
      {
        type: 'gain',
        params: { gain: 3, pan: 0, phase: false }
      }
    ]
  },
  
  'guitar-ambient': {
    id: 'guitar-ambient',
    name: 'Ambient Guitar',
    description: 'Spacious, atmospheric guitar',
    category: 'guitar',
    icon: 'guitar',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 150, lowGain: -3,
          mid1Freq: 600, mid1Gain: -1, mid1Q: 1,
          mid2Freq: 2000, mid2Gain: 1.5, mid2Q: 0.8,
          highFreq: 10000, highGain: 2
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -22, ratio: 2, attack: 0.03, release: 0.3,
          knee: 12, makeup: 1, mix: 0.5
        }
      },
      {
        type: 'delay',
        params: { time: 0.35, feedback: 0.4, stereoSpread: 0.3, filter: 5000, mix: 0.3 }
      },
      {
        type: 'reverb',
        params: { decay: 3, preDelay: 0.04, damping: 4000, size: 0.7, mix: 0.35 }
      }
    ]
  },

  // === KEYS/SYNTH ===
  'keys-warm': {
    id: 'keys-warm',
    name: 'Warm Keys',
    description: 'Rich, vintage keyboard sound',
    category: 'keys',
    icon: 'piano',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 100, lowGain: 1,
          mid1Freq: 400, mid1Gain: 1, mid1Q: 0.8,
          mid2Freq: 2500, mid2Gain: -1, mid2Q: 1,
          highFreq: 6000, highGain: -2
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -20, ratio: 2, attack: 0.02, release: 0.25,
          knee: 10, makeup: 1.5, mix: 0.6
        }
      },
      {
        type: 'chorus',
        params: { rate: 0.3, depth: 0.002, mix: 0.15 }
      }
    ]
  },
  
  'synth-bright': {
    id: 'synth-bright',
    name: 'Bright Synth',
    description: 'Crystal-clear synth presence',
    category: 'keys',
    icon: 'waves',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 80, lowGain: -1,
          mid1Freq: 500, mid1Gain: -2, mid1Q: 1.5,
          mid2Freq: 4000, mid2Gain: 3, mid2Q: 0.8,
          highFreq: 12000, highGain: 2.5
        }
      },
      {
        type: 'compressor',
        params: {
          threshold: -15, ratio: 3, attack: 0.005, release: 0.1,
          knee: 4, makeup: 2, mix: 0.8
        }
      }
    ]
  },
  
  'synth-pad': {
    id: 'synth-pad',
    name: 'Pad Space',
    description: 'Wide, atmospheric synth pads',
    category: 'keys',
    icon: 'waves',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 100, lowGain: -2,
          mid1Freq: 300, mid1Gain: -1, mid1Q: 1,
          mid2Freq: 2500, mid2Gain: 1, mid2Q: 0.8,
          highFreq: 8000, highGain: 1
        }
      },
      {
        type: 'chorus',
        params: { rate: 0.2, depth: 0.003, mix: 0.25 }
      },
      {
        type: 'reverb',
        params: { decay: 4, preDelay: 0.05, damping: 4000, size: 0.8, mix: 0.4 }
      }
    ]
  },

  // === UTILITY ===
  'utility-clean': {
    id: 'utility-clean',
    name: 'Clean Slate',
    description: 'Minimal processing, maximum clarity',
    category: 'utility',
    icon: 'sparkles',
    plugins: [
      {
        type: 'parametric-eq',
        params: {
          lowFreq: 80, lowGain: 0,
          mid1Freq: 400, mid1Gain: 0, mid1Q: 1,
          mid2Freq: 3000, mid2Gain: 0, mid2Q: 1,
          highFreq: 10000, highGain: 0
        }
      },
      {
        type: 'gain',
        params: { gain: 0, pan: 0, phase: false }
      }
    ]
  },
  
  'utility-glue': {
    id: 'utility-glue',
    name: 'Glue Compression',
    description: 'Light compression to gel the sound',
    category: 'utility',
    icon: 'link',
    plugins: [
      {
        type: 'compressor',
        params: {
          threshold: -24, ratio: 2, attack: 0.03, release: 0.3,
          knee: 12, makeup: 1, mix: 0.5
        }
      }
    ]
  },
  
  'utility-widener': {
    id: 'utility-widener',
    name: 'Stereo Width',
    description: 'Enhance stereo image',
    category: 'utility',
    icon: 'expand',
    plugins: [
      {
        type: 'chorus',
        params: { rate: 0.1, depth: 0.001, mix: 0.15 }
      },
      {
        type: 'delay',
        params: { time: 0.02, feedback: 0, stereoSpread: 0.5, filter: 8000, mix: 0.2 }
      }
    ]
  }
};

// Preset categories for filtering (track-specific)
export const TRACK_PRESET_CATEGORIES = {
  all: { name: 'All', icon: 'grid' },
  vocals: { name: 'Vocals', icon: 'mic' },
  drums: { name: 'Drums', icon: 'drum' },
  bass: { name: 'Bass', icon: 'music' },
  guitar: { name: 'Guitar', icon: 'guitar' },
  keys: { name: 'Keys/Synth', icon: 'piano' },
  utility: { name: 'Utility', icon: 'settings' },
  user: { name: 'My Presets', icon: 'user' }
};

// Local storage key for user track presets
const USER_TRACK_PRESETS_KEY = 'stringsauce_track_presets';

/**
 * Get user-saved track presets from localStorage
 */
export const getUserTrackPresets = () => {
  try {
    const saved = localStorage.getItem(USER_TRACK_PRESETS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (e) {
    console.error('Failed to load user track presets:', e);
    return {};
  }
};

/**
 * Save user track preset to localStorage
 */
export const saveUserTrackPreset = (preset) => {
  try {
    const userPresets = getUserTrackPresets();
    userPresets[preset.id] = {
      ...preset,
      category: 'user',
      createdAt: new Date().toISOString()
    };
    localStorage.setItem(USER_TRACK_PRESETS_KEY, JSON.stringify(userPresets));
    return true;
  } catch (e) {
    console.error('Failed to save user track preset:', e);
    return false;
  }
};

/**
 * Delete user track preset from localStorage
 */
export const deleteUserTrackPreset = (presetId) => {
  try {
    const userPresets = getUserTrackPresets();
    delete userPresets[presetId];
    localStorage.setItem(USER_TRACK_PRESETS_KEY, JSON.stringify(userPresets));
    return true;
  } catch (e) {
    console.error('Failed to delete user track preset:', e);
    return false;
  }
};

/**
 * Get all track presets (factory + user)
 */
export const getAllTrackPresets = () => {
  const userPresets = getUserTrackPresets();
  return { ...FACTORY_TRACK_PRESETS, ...userPresets };
};

/**
 * Serialize current track plugin chain to preset format
 */
export const serializeTrackChainToPreset = (plugins, name, description = '', category = 'user') => {
  return {
    id: `user-track-${Date.now()}`,
    name,
    description,
    category: 'user',
    originalCategory: category !== 'user' ? category : undefined,
    icon: 'bookmark',
    plugins: plugins.map(plugin => {
      const params = {};
      
      // Handle both Map and object formats for parameters
      if (plugin.parameters instanceof Map) {
        plugin.parameters.forEach((param, id) => {
          params[id] = param.getValue ? param.getValue() : param.value;
        });
      } else if (plugin.params) {
        Object.entries(plugin.params).forEach(([id, value]) => {
          params[id] = value;
        });
      }
      
      // Get plugin type from name
      const typeName = plugin.name?.toLowerCase().replace(/\s+/g, '-') || plugin.type || 'unknown';
      
      return {
        type: typeName === 'parametric eq' ? 'parametric-eq' : typeName,
        params
      };
    })
  };
};

/**
 * Get recommended presets based on track name
 */
export const getRecommendedPresets = (trackName) => {
  const name = (trackName || '').toLowerCase();
  const allPresets = getAllTrackPresets();
  const recommended = [];
  
  // Match based on common track name patterns
  if (name.includes('vocal') || name.includes('voice') || name.includes('vox')) {
    Object.values(allPresets).filter(p => p.category === 'vocals').forEach(p => recommended.push(p));
  }
  if (name.includes('drum') || name.includes('beat') || name.includes('kick') || name.includes('snare')) {
    Object.values(allPresets).filter(p => p.category === 'drums').forEach(p => recommended.push(p));
  }
  if (name.includes('bass')) {
    Object.values(allPresets).filter(p => p.category === 'bass').forEach(p => recommended.push(p));
  }
  if (name.includes('guitar') || name.includes('gtr')) {
    Object.values(allPresets).filter(p => p.category === 'guitar').forEach(p => recommended.push(p));
  }
  if (name.includes('key') || name.includes('piano') || name.includes('synth') || name.includes('pad')) {
    Object.values(allPresets).filter(p => p.category === 'keys').forEach(p => recommended.push(p));
  }
  
  return recommended.slice(0, 6); // Limit to 6 recommendations
};

export default {
  FACTORY_TRACK_PRESETS,
  TRACK_PRESET_CATEGORIES,
  getUserTrackPresets,
  saveUserTrackPreset,
  deleteUserTrackPreset,
  getAllTrackPresets,
  serializeTrackChainToPreset,
  getRecommendedPresets
};
